import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// lib/seo pulls the full team + guide datasets; load it off the critical path (prerendered head tags cover the first paint).
let seoModule = null;
const loadSeo = () => seoModule ? Promise.resolve(seoModule) : import("@/lib/seo").then((m) => (seoModule = m));

export const RouteSeo = () => {
  const { pathname } = useLocation();
  const [mod, setMod] = useState(seoModule);
  useEffect(() => { let on = true; loadSeo().then((m) => on && setMod(m)); return () => { on = false; }; }, []);
  // React 19 hoists Helmet tags natively (no data-rh reconciliation), drop the prerendered/static copies once Helmet takes over.
  useEffect(() => { if (mod) document.head.querySelectorAll('[data-rh="true"]').forEach((el) => el.remove()); }, [mod]);
  if (!mod) return null;
  const { getRouteSeo, orgSchema, websiteSchema, SITE_NAME } = mod;
  const seo = getRouteSeo(pathname);
  const image = seo.image;
  const routeSchemas = Array.isArray(seo.schema) ? seo.schema : seo.schema ? [seo.schema] : [];
  const graph = [orgSchema(), websiteSchema(), ...routeSchemas];

  return (
    <Helmet prioritizeSeoTags>
      <html lang={seo.lang || "en"} />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {seo.canonical && <link rel="canonical" href={seo.canonical} />}
      <meta name="robots" content={seo.noindex ? "noindex,nofollow" : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"} />
      {(seo.alternates || []).map(([hl, href]) => <link key={hl} rel="alternate" hrefLang={hl} href={href} />)}

      <meta property="og:type" content={pathname.startsWith("/team/") ? "profile" : seo.type || "website"} />
      <meta property="og:locale" content={seo.lang === "zh-Hans" ? "zh_SG" : "en_SG"} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={seo.ogTitle || seo.title} />
      <meta property="og:description" content={seo.ogDescription || seo.description} />
      {seo.canonical && <meta property="og:url" content={seo.canonical} />}
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:width" content="1200" />}
      {image && <meta property="og:image:height" content="630" />}
      {image && <meta property="og:image:type" content="image/png" />}
      {image && <meta property="og:image:secure_url" content={image} />}
      {image && <meta property="og:image:alt" content={seo.ogTitle || seo.title} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.ogTitle || seo.title} />
      <meta name="twitter:description" content={seo.ogDescription || seo.description} />
      {image && <meta name="twitter:image" content={image} />}

      {graph.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};
